export type StateTaxType = "none" | "flat" | "progressive";

export type StateTaxBracket = {
  upTo: number | null; // null = no upper cap (top bracket)
  rate: number; // as decimal (0.099 = 9.9%)
  note?: string;
};

export type StateTaxSummary = {
  code: string;
  name: string;
  type: StateTaxType;
  flatRate?: number;
  range?: { min: number; max: number };
  brackets?: StateTaxBracket[];
  footnote?: string;
};

export const STATE_TAX_SUMMARY_2024: Record<string, StateTaxSummary> = {
  OR: {
    code: "OR",
    name: "Oregon",
    type: "progressive",
    range: { min: 0.095, max: 0.198 },
    footnote:
      "Oregon taxes are progressive. Standard deduction applies; local transit/tri-met not included here.",
    brackets: [
      { upTo: 4150, rate: 0.095 },
      { upTo: 10400, rate: 0.135 },
      { upTo: 125000, rate: 0.175 },
      { upTo: null, rate: 0.198 },
    ],
  },

  AK: { code: "AK", name: "Alaska", type: "none" },
  FL: { code: "FL", name: "Florida", type: "none" },
  NV: { code: "NV", name: "Nevada", type: "none" },
  SD: { code: "SD", name: "South Dakota", type: "none" },
  TN: { code: "TN", name: "Tennessee", type: "none", footnote: "No wage tax." },
  TX: { code: "TX", name: "Texas", type: "none" },
  WA: { code: "WA", name: "Washington", type: "none", footnote: "No wage tax." },
  WY: { code: "WY", name: "Wyoming", type: "none" },
  NH: {
    code: "NH",
    name: "New Hampshire",
    type: "none",
    footnote: "No wage tax; limited interest/dividend tax has been phasing out.",
  },

  AZ: { code: "AZ", name: "Arizona", type: "flat", flatRate: 0.05 },
  CO: { code: "CO", name: "Colorado", type: "flat", flatRate: 0.088 },
  ID: { code: "ID", name: "Idaho", type: "flat", flatRate: 0.116 },
  IL: { code: "IL", name: "Illinois", type: "flat", flatRate: 0.099 },
  IN: { code: "IN", name: "Indiana", type: "flat", flatRate: 0.063 },
  KY: { code: "KY", name: "Kentucky", type: "flat", flatRate: 0.08 },
  MI: { code: "MI", name: "Michigan", type: "flat", flatRate: 0.085 },
  MS: { code: "MS", name: "Mississippi", type: "flat", flatRate: 0.10 },
  NC: { code: "NC", name: "North Carolina", type: "flat", flatRate: 0.09 },
  PA: { code: "PA", name: "Pennsylvania", type: "flat", flatRate: 0.0614 },
  UT: { code: "UT", name: "Utah", type: "flat", flatRate: 0.093 },

  AL: { code: "AL", name: "Alabama", type: "progressive", range: { min: 0.04, max: 0.10 } },
  AR: { code: "AR", name: "Arkansas", type: "progressive", range: { min: 0.0, max: 0.098 } },
  CA: { code: "CA", name: "California", type: "progressive", range: { min: 0.02, max: 0.266 } },
  CT: { code: "CT", name: "Connecticut", type: "progressive", range: { min: 0.06, max: 0.1398 } },
  DC: {
    code: "DC",
    name: "District of Columbia",
    type: "progressive",
    range: { min: 0.08, max: 0.215 },
  },
  DE: { code: "DE", name: "Delaware", type: "progressive", range: { min: 0.0, max: 0.132 } },
  GA: { code: "GA", name: "Georgia", type: "progressive", range: { min: 0.02, max: 0.115 } },
  HI: { code: "HI", name: "Hawaii", type: "progressive", range: { min: 0.028, max: 0.22 } },
  IA: { code: "IA", name: "Iowa", type: "progressive", range: { min: 0.0, max: 0.106 } },
  KS: { code: "KS", name: "Kansas", type: "progressive", range: { min: 0.062, max: 0.114 } },
  LA: { code: "LA", name: "Louisiana", type: "progressive", range: { min: 0.037, max: 0.085 } },
  MA: {
    code: "MA",
    name: "Massachusetts",
    type: "progressive",
    range: { min: 0.10, max: 0.18 },
    footnote: "MA applies a higher rate to specific high-income classes.",
  },
  MD: { code: "MD", name: "Maryland", type: "progressive", range: { min: 0.04, max: 0.115 } },
  ME: { code: "ME", name: "Maine", type: "progressive", range: { min: 0.116, max: 0.143 } },
  MN: { code: "MN", name: "Minnesota", type: "progressive", range: { min: 0.107, max: 0.197 } },
  MO: { code: "MO", name: "Missouri", type: "progressive", range: { min: 0.0, max: 0.099 } },
  MT: { code: "MT", name: "Montana", type: "progressive", range: { min: 0.02, max: 0.135 } },
  NE: { code: "NE", name: "Nebraska", type: "progressive", range: { min: 0.0492, max: 0.1328 } },
  NJ: { code: "NJ", name: "New Jersey", type: "progressive", range: { min: 0.028, max: 0.215 } },
  NM: { code: "NM", name: "New Mexico", type: "progressive", range: { min: 0.034, max: 0.118 } },
  NY: { code: "NY", name: "New York", type: "progressive", range: { min: 0.08, max: 0.218 } },
  OH: { code: "OH", name: "Ohio", type: "progressive", range: { min: 0.0, max: 0.075 } },
  OK: { code: "OK", name: "Oklahoma", type: "progressive", range: { min: 0.005, max: 0.095 } },
  RI: {
    code: "RI",
    name: "Rhode Island",
    type: "progressive",
    range: { min: 0.075, max: 0.1198 },
  },
  SC: { code: "SC", name: "South Carolina", type: "progressive", range: { min: 0.0, max: 0.134 } },
  VA: { code: "VA", name: "Virginia", type: "progressive", range: { min: 0.04, max: 0.115 } },
  VT: { code: "VT", name: "Vermont", type: "progressive", range: { min: 0.067, max: 0.175 } },
  WI: { code: "WI", name: "Wisconsin", type: "progressive", range: { min: 0.0708, max: 0.153 } },
  WV: {
    code: "WV",
    name: "West Virginia",
    type: "progressive",
    range: { min: 0.0552, max: 0.1024 },
  },
};
